import "@/App.css";
import { BrowserRouter, Navigate, useLocation } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { resolveCustomDomain, needsDomainResolution, customDomainEntryPath } from './utils/customDomain';
import ErrorBoundary from "./components/ErrorBoundary";
import TenantStatusWrapper from "./components/TenantStatusWrapper";
import { AuthProvider } from "./contexts/AuthContext";
import { CartProvider } from "./contexts/CartContext";
import { CurrencyProvider } from "./contexts/CurrencyContext";
import { TimezoneProvider } from "./contexts/TimezoneContext";
import ToastProvider from "./components/providers/ToastProvider";
import DeploymentScreen from "./components/DeploymentScreen";
import { getTenantSubdomain, isMarketplaceHost } from "./utils/tenant";
import TenantRoutes from "./routes/TenantRoutes";
import MainRoutes from "./routes/MainRoutes";
import ScrollToTop from "./components/ScrollToTop";
import AdminSessionMonitor from './components/auth/AdminSessionMonitor';
import PublicMarketplaceGate from "./components/PublicMarketplaceGate";

import MaintenanceOverlay from "./components/MaintenanceOverlay";

// Marketplace v1 (M1e) — the marketplace FRONT DOOR host (marketplace.plateos.io)
// renders the cross-restaurant /marketplace app as its root, NOT a per-restaurant
// tenant storefront. Resolve it first so it can never be misread as a tenant
// (getTenantSubdomain also short-circuits on it, so this is null on that host).
function DomainRouteGuard({ children }) {
  const location = useLocation();
  const destination = customDomainEntryPath(location.pathname);
  return destination ? <Navigate replace to={destination + location.search + location.hash} /> : children;
}

function ResolvedApp() {
  const marketplaceHost = isMarketplaceHost();
  const tenantSubdomain = marketplaceHost ? null : getTenantSubdomain();
  return (
    <ErrorBoundary>
      <MaintenanceOverlay>
      <ToastProvider>
        <CurrencyProvider>
          <CartProvider>
            {process.env.REACT_APP_BACKEND_URL?.includes('preview.emergentagent.com') && <DeploymentScreen />}
            <AuthProvider>
              <TimezoneProvider>
              <div className="App">
                <BrowserRouter>
                  {/* v5.48 (founder): ONE site-wide scroll reset, above BOTH route
                      trees, so no page can be built without it. */}
                  <ScrollToTop />
                  <AdminSessionMonitor />
                  <PublicMarketplaceGate>
                  <DomainRouteGuard>
                  {marketplaceHost ? (
                    // The marketplace front door: reuse the public route tree but
                    // render MarketplacePage at "/" (no tenant to status-check).
                    <MainRoutes marketplaceRoot />
                  ) : tenantSubdomain ? (
                    <TenantStatusWrapper tenantSubdomain={tenantSubdomain}>
                      <TenantRoutes tenantSubdomain={tenantSubdomain} />
                    </TenantStatusWrapper>
                  ) : (
                    <MainRoutes />
                  )}
                  </DomainRouteGuard>
                  </PublicMarketplaceGate>
                </BrowserRouter>
              </div>
              </TimezoneProvider>
            </AuthProvider>
          </CartProvider>
        </CurrencyProvider>
      </ToastProvider>
      </MaintenanceOverlay>
    </ErrorBoundary>
  );
}

function App() {
  const [ready, setReady] = useState(!needsDomainResolution());
  const [error, setError] = useState('');
  useEffect(() => {
    if (!needsDomainResolution()) return;
    let mounted = true;
    resolveCustomDomain().then(() => {
      if (!mounted) return;
      const path = customDomainEntryPath(window.location.pathname);
      if (path) window.history.replaceState(null, '', path + window.location.search + window.location.hash);
      setReady(true);
    }).catch(e => { if (mounted) setError(e.message); });
    return () => { mounted = false; };
  }, []);
  if (!ready) return <main className="p-8 text-center" role="status">{error || 'Connecting to your store…'}</main>;
  return <ResolvedApp />;
}
export default App;
